import { Button, Checkbox, Flex, Spin, Typography, message, theme } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { DeclineAction } from "../../../components/Esign/signing/DeclineAction.tsx"
import { DownloadCopy } from "../../../components/Esign/signing/DownloadCopy.tsx"
import { shareInFlight } from "../../../components/Esign/signing/in-flight.ts"
import { TOUCH } from "../../../components/Esign/signing/touch.ts"
import { fetchDisclosure, giveConsent } from "../api/signing.services"
import { createPublicTransport } from "../api/signing.transport"
import type { Disclosure, SigningRefusal } from "../types/signing.types"
import SigningDocumentView from "./SigningDocumentView"

const { Paragraph, Title } = Typography

interface Props {
  session: string
  documentName: string
  documentUrl: string | null
  documentBlob: Blob | null
  /** Moving on before it lands would read the document twice. */
  documentReady: boolean
  onAgreed: () => void
  onDeclined: () => void
  onRefused: (refusal: SigningRefusal) => void
}

/**
 * The disclosure and the agreement.
 *
 * The words come from the server exactly as they were frozen when the document
 * was sent, and the hash goes back with the agreement, so nobody can be recorded
 * as having agreed to text they were not shown.
 */
const ConsentStep: React.FC<Props> = ({
  session,
  documentName,
  documentUrl,
  documentBlob,
  documentReady,
  onAgreed,
  onDeclined,
  onRefused
}) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [disclosure, setDisclosure] = useState<Disclosure | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [saving, setSaving] = useState(false)
  const transport = useMemo(() => createPublicTransport(session), [session])
  // Each showing of the disclosure is recorded, and strict mode starts the load twice.
  const reads = useRef<{
    session: string
    read: () => ReturnType<typeof fetchDisclosure>
  } | null>(null)

  const load = useCallback(async () => {
    if (reads.current?.session !== session)
      reads.current = {
        session,
        read: shareInFlight(() => fetchDisclosure(session))
      }
    const response = await reads.current.read()
    if (!response.ok || !response.data) {
      onRefused(response.refusal ?? { reason: "unknown" })
      return null
    }
    setDisclosure(response.data)
    return response.data
  }, [session, onRefused])

  useEffect(() => {
    void load().then((loaded) => {
      if (loaded?.already_agreed) onAgreed()
    })
  }, [load, onAgreed])

  const submit = useCallback(async () => {
    if (!disclosure) return
    setSaving(true)
    const response = await giveConsent(session, disclosure.sha256)
    setSaving(false)

    if (response.ok) {
      onAgreed()
      return
    }
    if (response.refusal?.reason === "stale_disclosure") {
      // Fetching the new wording rather than reloading the page: the session
      // lives in memory and the token is already wiped from the address bar, so
      // a reload would land the reader on a page with no link at all.
      void message.warning(t("signing.consent.reread"))
      setAgreed(false)
      void load()
      return
    }
    onRefused(response.refusal ?? { reason: "unknown" })
  }, [disclosure, session, onAgreed, onRefused, load, t])

  if (!disclosure) {
    return (
      <Flex justify="center" style={{ padding: "48px 0" }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      <Title level={5} style={{ margin: 0 }}>
        {t("signing.consent.title")}
      </Title>

      {/* Beside the words being agreed to, not only afterwards: §101(c)(1)(C)(ii)
          asks for consent given in a way that shows the consumer can read the
          format the record will come in. */}
      {documentUrl && (
        <SigningDocumentView
          session={session}
          documentBlob={documentBlob}
          height="40vh"
          withFields={false}
        />
      )}

      <div
        style={{
          maxHeight: 320,
          overflowY: "auto",
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadius,
          padding: token.padding
        }}
      >
        <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
          {disclosure.text}
        </Paragraph>
      </div>

      <Checkbox
        checked={agreed}
        onChange={(event) => setAgreed(event.target.checked)}
      >
        {t("signing.consent.checkbox")}
      </Checkbox>

      <Button
        type="primary"
        size="large"
        block
        disabled={!agreed || !documentReady}
        loading={saving || (agreed && !documentReady)}
        style={TOUCH}
        onClick={() => void submit()}
      >
        {t("signing.consent.continue")}
      </Button>

      <Flex gap={8} wrap justify="space-between">
        <DownloadCopy
          download={() => transport.download(documentName)}
          label={t("signing.document.download")}
        />
        <DeclineAction
          decline={(reason) => transport.decline(reason)}
          onDeclined={onDeclined}
        />
      </Flex>
    </Flex>
  )
}

export default ConsentStep
